import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Shield, Lock, Mail, Phone } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ConsultationForm = () => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country: '',
    natureOfMatter: '',
    preferredContact: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({ country: '', natureOfMatter: '' });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSelectChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value,
    });
    // Clear the field's error as soon as the user makes a selection
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validate = () => {
    const newErrors = {
      country: formData.country ? '' : 'Please select your country or jurisdiction.',
      natureOfMatter: formData.natureOfMatter ? '' : 'Please select the nature of your matter.',
    };
    setErrors(newErrors);
    return !newErrors.country && !newErrors.natureOfMatter;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required dropdowns before submitting
    if (!validate()) {
      toast.error('A Few Details Needed', {
        description: 'Please select your country and the nature of your matter before submitting.',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Call backend API
      const response = await axios.post(`${API}/consultations`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        country: formData.country,
        nature_of_matter: formData.natureOfMatter,
        preferred_contact: formData.preferredContact || null,
        message: formData.message || null,
      });

      if (response.status === 201) {
        toast.success('Consultation Request Received', {
          description: 'Thank you for your interest. We will contact you within 24 hours to arrange a confidential consultation.',
        });

        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          country: '',
          natureOfMatter: '',
          preferredContact: '',
          message: '',
        });
        setErrors({ country: '', natureOfMatter: '' });
      }
    } catch (error) {
      console.error('Error submitting consultation:', error);
      
      let errorMessage = 'Unable to submit your request. Please try again or contact us directly.';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error('Submission Failed', {
        description: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="consultation" className="relative py-24 md:py-32 bg-[#0f1419] overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'radial-gradient(circle at 2px 2px, #c9a961 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        {/* Section header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <span className="text-sm text-[#c9a961] uppercase tracking-wider mb-4 block">Get In Touch</span>
          <h2 className="font-serif text-4xl md:text-6xl text-white mb-6 leading-tight">
            Request Private Consultation
          </h2>
          <p className="text-lg text-gray-400 max-w-3xl mx-auto leading-relaxed">
            All inquiries are handled with strict confidentiality and protected by attorney-client privilege
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Privacy assurance */}
          <motion.div
            initial={{ opacity: 0, x: -60 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -60 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-8"
          >
            <div>
              <h3 className="font-serif text-3xl text-white mb-6">Confidential Inquiry</h3>
              <p className="text-gray-400 leading-relaxed mb-8">
                Your privacy is paramount. All consultation requests are handled with the utmost discretion,
                and all communications are protected under attorney-client privilege from the moment of first contact.
              </p>
            </div>

            {/* Privacy features */}
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-[#c9a961]/10 rounded-full flex items-center justify-center">
                  <Shield className="w-6 h-6 text-[#c9a961]" />
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-2">Attorney-Client Privilege</h4>
                  <p className="text-gray-400 text-sm">
                    All communications are protected from the moment of first contact
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-[#c9a961]/10 rounded-full flex items-center justify-center">
                  <Lock className="w-6 h-6 text-[#c9a961]" />
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-2">Secure Communications</h4>
                  <p className="text-gray-400 text-sm">
                    Encrypted channels available for sensitive matters
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-[#c9a961]/10 rounded-full flex items-center justify-center">
                  <Mail className="w-6 h-6 text-[#c9a961]" />
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-2">Discreet Contact</h4>
                  <p className="text-gray-400 text-sm">
                    Direct communication with Johann C Strauss
                  </p>
                </div>
              </div>
            </div>

            {/* Alternative contact */}
            <div className="p-6 bg-[#1a2332]/50 border border-[#c9a961]/20">
              <h4 className="text-white font-semibold mb-4">Prefer to Call or Message?</h4>
              <a
                href="https://wa.me/27784312235"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-link"
                className="flex items-center gap-3 text-[#c9a961] mb-3 hover:text-[#e0c078] transition-colors duration-300"
              >
                <Phone className="w-5 h-5" />
                <span className="text-lg">+27 78 431 2235</span>
                <span className="text-xs uppercase tracking-wider text-gray-500">Call / WhatsApp</span>
              </a>
              <div className="flex items-start gap-3 text-gray-400 mb-2">
                <Mail className="w-5 h-5 flex-shrink-0 mt-0.5 text-[#c9a961]" />
                <span className="text-sm">
                  For written inquiries, please use the confidential form. We will respond by secure email.
                </span>
              </div>
              <p className="text-gray-400 text-sm">
                Available for confidential consultations by appointment
              </p>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: 60 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: 60 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <form onSubmit={handleSubmit} className="space-y-6 bg-[#1a2332]/30 p-8 md:p-10 border border-[#c9a961]/20">
              {/* Name */}
              <div>
                <Label htmlFor="name" className="text-white mb-2 block">
                  Full Name *
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="bg-[#0f1419]/50 border-[#c9a961]/30 text-white focus:border-[#c9a961] rounded-none"
                  placeholder="Your full name"
                />
              </div>

              {/* Email */}
              <div>
                <Label htmlFor="email" className="text-white mb-2 block">
                  Email Address *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="bg-[#0f1419]/50 border-[#c9a961]/30 text-white focus:border-[#c9a961] rounded-none"
                  placeholder="your.email@example.com"
                />
              </div>

              {/* Phone */}
              <div>
                <Label htmlFor="phone" className="text-white mb-2 block">
                  Phone Number
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  className="bg-[#0f1419]/50 border-[#c9a961]/30 text-white focus:border-[#c9a961] rounded-none"
                  placeholder="+1 (555) 000-0000"
                />
              </div>

              {/* Country */}
              <div>
                <Label htmlFor="country" className="text-white mb-2 block">
                  Country/Jurisdiction *
                </Label>
                <Select value={formData.country} onValueChange={(value) => handleSelectChange('country', value)}>
                  <SelectTrigger
                    data-testid="country-select-trigger"
                    aria-invalid={!!errors.country}
                    className={`bg-[#0f1419]/50 text-white rounded-none ${
                      errors.country
                        ? 'border-red-500 focus:border-red-500'
                        : 'border-[#c9a961]/30 focus:border-[#c9a961]'
                    }`}
                  >
                    <SelectValue placeholder="Select your country" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a2332] border-[#c9a961]/30 text-white">
                    <SelectItem value="usa">United States</SelectItem>
                    <SelectItem value="uk">United Kingdom</SelectItem>
                    <SelectItem value="eu">European Union</SelectItem>
                    <SelectItem value="me">Middle East</SelectItem>
                    <SelectItem value="cyprus">Cyprus</SelectItem>
                    <SelectItem value="china">Mainland China</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                {errors.country && (
                  <p data-testid="country-error" className="text-red-400 text-sm mt-2">
                    {errors.country}
                  </p>
                )}
              </div>

              {/* Nature of Matter */}
              <div>
                <Label htmlFor="natureOfMatter" className="text-white mb-2 block">
                  Nature of Matter *
                </Label>
                <Select value={formData.natureOfMatter} onValueChange={(value) => handleSelectChange('natureOfMatter', value)}>
                  <SelectTrigger
                    data-testid="nature-select-trigger"
                    aria-invalid={!!errors.natureOfMatter}
                    className={`bg-[#0f1419]/50 text-white rounded-none ${
                      errors.natureOfMatter
                        ? 'border-red-500 focus:border-red-500'
                        : 'border-[#c9a961]/30 focus:border-[#c9a961]'
                    }`}
                  >
                    <SelectValue placeholder="Select matter type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a2332] border-[#c9a961]/30 text-white">
                    <SelectItem value="wealth">Wealth Protection & Asset Structuring</SelectItem>
                    <SelectItem value="trust">Trust & Banking Advisory</SelectItem>
                    <SelectItem value="business">Cross-Border Business Strategy</SelectItem>
                    <SelectItem value="dispute">Dispute Resolution</SelectItem>
                    <SelectItem value="tech">IT/Telecommunications</SelectItem>
                    <SelectItem value="banking">International Banking Dispute</SelectItem>
                    <SelectItem value="other">Other Confidential Matter</SelectItem>
                  </SelectContent>
                </Select>
                {errors.natureOfMatter && (
                  <p data-testid="nature-error" className="text-red-400 text-sm mt-2">
                    {errors.natureOfMatter}
                  </p>
                )}
              </div>

              {/* Preferred Contact */}
              <div>
                <Label htmlFor="preferredContact" className="text-white mb-2 block">
                  Preferred Contact Method
                </Label>
                <Select onValueChange={(value) => handleSelectChange('preferredContact', value)}>
                  <SelectTrigger className="bg-[#0f1419]/50 border-[#c9a961]/30 text-white focus:border-[#c9a961] rounded-none">
                    <SelectValue placeholder="Select contact method" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a2332] border-[#c9a961]/30 text-white">
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="phone">Phone</SelectItem>
                    <SelectItem value="secure">Secure Message</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Message */}
              <div>
                <Label htmlFor="message" className="text-white mb-2 block">
                  Describe Your Matter &mdash; Please List Your Issue(s)
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={8}
                  data-testid="consultation-message-input"
                  className="bg-[#0f1419]/50 border-[#c9a961]/30 text-white focus:border-[#c9a961] rounded-none resize-y min-h-[180px]"
                  placeholder="Please describe your situation and list the specific issue(s) you would like to discuss. Include any relevant background, jurisdictions involved, and desired outcomes. All communications are treated in the strictest confidence."
                />
                <p className="text-xs text-gray-500 mt-2">
                  The more detail you provide, the more precisely we can prepare for your confidential consultation.
                </p>
              </div>

              {/* Privacy notice */}
              <div className="text-xs text-gray-500 leading-relaxed">
                By submitting this form, you acknowledge that communications may be protected by attorney-client privilege.
                All information provided will be handled with strict confidentiality.
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={isSubmitting}
                data-testid="consultation-submit-btn"
                className="w-full bg-[#c9a961] hover:bg-[#b89851] text-[#0f1419] font-semibold py-6 text-lg rounded-none hover:scale-105 hover:shadow-xl hover:shadow-[#c9a961]/30"
                style={{ transition: 'all 0.3s' }}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Confidential Inquiry'}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ConsultationForm;
